import { getTtsVoice, pickVoiceForLang } from "./ttsVoice";

// Native device TTS via the Web Speech API. Available in iOS WKWebView
// (Capacitor) and all modern browsers. Used as:
//   - The primary playback path for history bubbles + phrasebook entries
//     where no pre-rendered audio_base64 is available.
//   - The fallback path when the backend /tts endpoint times out or fails.
//
// Why prefer this over the backend OpenAI TTS for replay?
//   * Instant — no network roundtrip (critical on cellular in foreign
//     countries where latency is high).
//   * Always available offline (the whole point of a travel app).
//   * Plays synchronously inside the user-gesture window, so iOS audio
//     autoplay restrictions never bite us.
//   * Native voices for the user's language are usually excellent on iOS.

// Maps the human-readable language names we use across the app to the
// BCP-47 tags the Web Speech API expects. Only the most common travel
// languages are listed; anything not in the map falls back to whatever
// voice the device picks for the language string.
const BCP47 = {
  English: "en-US",
  Spanish: "es-ES",
  French: "fr-FR",
  German: "de-DE",
  Italian: "it-IT",
  Portuguese: "pt-PT",
  Japanese: "ja-JP",
  Korean: "ko-KR",
  Mandarin: "zh-CN",
  "Chinese (Simplified)": "zh-CN",
  "Chinese (Traditional)": "zh-TW",
  Cantonese: "yue-HK",
  Russian: "ru-RU",
  Arabic: "ar-SA",
  Hindi: "hi-IN",
  Thai: "th-TH",
  Vietnamese: "vi-VN",
  Hebrew: "he-IL",
  Turkish: "tr-TR",
  Polish: "pl-PL",
  Dutch: "nl-NL",
  Swedish: "sv-SE",
  Norwegian: "nb-NO",
  Danish: "da-DK",
  Finnish: "fi-FI",
  Czech: "cs-CZ",
  Greek: "el-GR",
  Indonesian: "id-ID",
  Malay: "ms-MY",
  Tagalog: "fil-PH",
  Filipino: "fil-PH",
  Romanian: "ro-RO",
  Hungarian: "hu-HU",
  Ukrainian: "uk-UA",
  Bulgarian: "bg-BG",
};

export function isNativeTTSAvailable() {
  return (
    typeof window !== "undefined" &&
    typeof window.speechSynthesis !== "undefined" &&
    typeof window.SpeechSynthesisUtterance !== "undefined"
  );
}

// ---------------------------------------------------------------------
// Voice list loading. On iOS WKWebView (and Safari) `getVoices()` returns
// an empty array the FIRST time it's called after page load — the voice
// catalog is hydrated asynchronously and the `voiceschanged` event fires
// when it's ready. If we don't wait, our gender-preference picker can't
// find any voice → the utterance plays with whatever default the system
// chooses (often female), which the user perceives as "Male voice
// broken". This module-level kickoff starts the load as early as
// possible so by the time the user taps a speaker the list is ready.
// ---------------------------------------------------------------------
let voicesReady = false;
const voicesReadyCallbacks = [];

function notifyVoicesReady() {
  voicesReady = true;
  while (voicesReadyCallbacks.length) {
    try {
      voicesReadyCallbacks.shift()();
    } catch {
      /* listener threw — ignore so the queue keeps draining */
    }
  }
}

function primeVoices() {
  if (!isNativeTTSAvailable()) return;
  const synth = window.speechSynthesis;
  // Immediate call — sometimes voices are already cached at this point
  // (e.g. when the user has used TTS earlier in the session).
  const list = synth.getVoices();
  if (list && list.length > 0) {
    notifyVoicesReady();
    return;
  }
  // Otherwise wait for the `voiceschanged` event. Some browsers fire it
  // multiple times — we only care about the first useful one.
  const handler = () => {
    const updated = synth.getVoices();
    if (updated && updated.length > 0) {
      synth.removeEventListener("voiceschanged", handler);
      notifyVoicesReady();
    }
  };
  synth.addEventListener("voiceschanged", handler);
  // Belt-and-suspenders: some iOS WKWebViews never fire `voiceschanged`
  // until the FIRST speak() call. Poll a few times in case the event
  // never arrives.
  let polled = 0;
  const poller = setInterval(() => {
    polled++;
    const updated = synth.getVoices();
    if (updated && updated.length > 0) {
      clearInterval(poller);
      synth.removeEventListener("voiceschanged", handler);
      notifyVoicesReady();
    } else if (polled > 20) {
      // Give up after ~4s — the device genuinely has no voices, in
      // which case we'll just play with the system default later.
      clearInterval(poller);
      synth.removeEventListener("voiceschanged", handler);
      notifyVoicesReady();
    }
  }, 200);
}

// Kick off voice priming on module load so by the time the user taps a
// speaker, voices are usually ready.
if (typeof window !== "undefined") {
  primeVoices();
}

function whenVoicesReady(callback) {
  if (voicesReady) {
    callback();
  } else {
    voicesReadyCallbacks.push(callback);
  }
}

/**
 * Speak text using the device's native TTS engine.
 *
 * Returns true if speech was successfully kicked off (or queued behind
 * the voice-list load), false otherwise so callers can decide whether
 * to retry via a different path.
 *
 * @param {string} text  - The text to speak.
 * @param {string} lang  - Human-readable language name ("Japanese") or a
 *                         BCP-47 tag ("ja-JP"). Optional.
 * @param {number} rate  - Playback rate (1.0 = normal). Optional.
 */
export function speakNative(text, lang, rate = 1.0) {
  if (!isNativeTTSAvailable() || !text) return false;
  try {
    const synth = window.speechSynthesis;
    const bcp47 = lang ? BCP47[lang] || lang : null;

    // Pre-flight the picker SYNCHRONOUSLY so the caller can decide to
    // fall back to OpenAI TTS if no acceptable native voice exists for
    // this language. Returning false here lets `playTTS` skip native
    // entirely and avoid the OS picking a creepy compact voice on its
    // own. We only short-circuit when the voices list is loaded;
    // otherwise we still queue performSpeak() and let pickVoiceForLang
    // decide once the list arrives.
    if (voicesReady) {
      const preflight = pickVoiceForLang(bcp47, getTtsVoice());
      if (!preflight) return false;
    }

    // Cancel any in-flight utterance so taps in quick succession behave
    // like "interrupt and play the new one" rather than queuing.
    synth.cancel();

    const performSpeak = () => {
      try {
        const preferred = pickVoiceForLang(bcp47, getTtsVoice());
        // If no safe voice exists for the language, do NOT speak — the
        // OS default could be a novelty voice. Caller will fall back to
        // OpenAI TTS via the rejected return value above (or via the
        // audio_voice mismatch path).
        if (!preferred) return;
        const u = new window.SpeechSynthesisUtterance(text);
        if (bcp47) {
          u.lang = bcp47;
        }
        u.voice = preferred;
        // Some iOS versions reset u.lang when voice is assigned; set
        // it again so the utterance respects the language tag.
        if (bcp47) u.lang = bcp47;
        u.rate = Math.max(0.5, Math.min(2.0, rate));
        u.volume = 1;
        synth.speak(u);
      } catch {
        /* SpeechSynthesisUtterance constructor blew up — ignore */
      }
    };

    // iOS Safari has a known quirk: calling synth.cancel() immediately
    // followed by synth.speak() in the same microtask can drop the
    // speak() entirely. A 50ms delay between cancel and speak avoids
    // this, AND it gives the voice list time to finish loading if it
    // wasn't ready when this module was first imported.
    if (voicesReady) {
      setTimeout(performSpeak, 50);
    } else {
      whenVoicesReady(() => setTimeout(performSpeak, 50));
    }
    return true;
  } catch {
    return false;
  }
}

export function stopNativeTTS() {
  if (!isNativeTTSAvailable()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* element disposed — ignore */
  }
}
